// Display formatters following the redesign copy rules
// (Design guidelines §8 "Copy & formats").
import * as dayjs from "dayjs";

const toNumber = value => {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
};

// 1480 → "Rs 1,480"; -65 → "− Rs 65" (true minus sign)
export const formatPrice = value => {
  const n = Math.round(toNumber(value));
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `− Rs ${abs}` : `Rs ${abs}`;
};

// 10 → "10% off"
export const formatDiscount = percentage =>
  `${Math.round(toNumber(percentage))}% off`;

// Percentage saved between two prices: (435, 392) → "10% off"
export const formatSaving = (was, now) => {
  const w = toNumber(was);
  if (!w) return "";
  return formatDiscount((1 - toNumber(now) / w) * 100);
};

// Same meridiem: "2:30 – 5:30 pm"; different: "11:30 am – 2:30 pm"
export const formatSlot = (start, end) => {
  const s = dayjs(start);
  const e = dayjs(end);
  const time = d => d.format("h:mm");
  if (s.format("a") === e.format("a")) {
    return `${time(s)} – ${time(e)} ${e.format("a")}`;
  }
  return `${time(s)} ${s.format("a")} – ${time(e)} ${e.format("a")}`;
};

// "Wed, 30 Sep" this year, "Wed, 30 Sep 2025" otherwise
export const formatDate = date => {
  const d = dayjs(date);
  return d.year() === dayjs().year()
    ? d.format("ddd, D MMM")
    : d.format("ddd, D MMM YYYY");
};

// "03001234567" | "923001234567" | "+92 - 3001234567" → "+92 300 1234567"
export const formatPhone = phone => {
  const digits = String(phone || "").replace(/\D/g, "");
  const local = digits.replace(/^(92|0)/, "");
  if (local.length !== 10) return phone || "";
  return `+92 ${local.slice(0, 3)} ${local.slice(3)}`;
};

const UNIT_ALIASES = [
  [/^(pieces?|pcs?|pc)$/i, "pcs"],
  [/^(kgs?|kilograms?)$/i, "kg"],
  [/^(grams?|gms?|gm|g)$/i, "g"],
  [/^(ltrs?|litres?|liters?|l)$/i, "L"],
  [/^(ml|millilitres?|milliliters?)$/i, "ml"],
  [/^dozens?$/i, "dozen"]
];

// "12 piece" → "12 pcs", "1kg" → "1 kg", "3 ltr" → "3 L"; unknown text passes through
export const formatPackSize = size => {
  if (!size) return "";
  const m = String(size)
    .trim()
    .match(/^([\d.]+)\s*([a-zA-Z]+)$/);
  if (!m) return String(size).trim();
  const [, amount, unit] = m;
  const alias = UNIT_ALIASES.find(([re]) => re.test(unit));
  return `${amount} ${alias ? alias[1] : unit}`;
};

// 1 → "1 item", 4 → "4 items"
export const formatItemCount = count =>
  `${count} ${count === 1 ? "item" : "items"}`;
